import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_CONFIG } from '../config/api.config';
import { map, catchError } from 'rxjs/operators';
import { BehaviorSubject, of } from 'rxjs';
import { GeoService } from './geo.service';

export type Currency = 'USD' | 'EUR' | 'GBP' | 'AED' | 'SAR' | 'EGP' | 'QAR';

@Injectable({
    providedIn: 'root'
})
export class CurrencyService {
    private http = inject(HttpClient);
    private geoService = inject(GeoService);
    readonly currentCurrency = signal<Currency>('USD');
    private currencyChanges = new BehaviorSubject<Currency>('USD');
    readonly currencyChanges$ = this.currencyChanges.asObservable();
    private readonly supported: Currency[] = ['USD', 'EUR', 'GBP', 'AED', 'SAR', 'EGP', 'QAR'];
    // Currency rates are served by the vNext Public service (cached server-side).
    private readonly rateApiUrl = `${API_CONFIG.LEGACY_PUBLIC_API_URL}/currency/rates`;
    private readonly ratesStorageKey = 'currency_rates_v2';
    private readonly legacyRatesKey = 'currency_rates';
    private readonly legacyTimestampKey = 'currency_rates_timestamp';
    private readonly staleMs = 60 * 60 * 1000; // 1h
    private inflight: any = null;
    private baseCurrency = 'USD';
    private lastFetchedAt = 0;

    // Exchange rates relative to USD. Populated from localStorage cache or the Public API.
    private rates: Record<string, number> = {};

    constructor() {
        // Load from local storage if available
        const saved = (localStorage.getItem('currency') as Currency) || null;
        if (saved && this.supported.includes(saved)) {
            this.currentCurrency.set(saved);
            this.currencyChanges.next(saved);
        } else {
            const detected = this.detectDefaultCurrency();
            this.currentCurrency.set(detected);
            this.currencyChanges.next(detected);
        }
        this.loadCachedRates();
        this.maybeRefreshRates();
        void this.maybeDetectCurrencyFromIp();
    }

    private detectDefaultCurrency(): Currency {
        const cached = this.geoService.peekGeoFromIpCache();
        const ipCountryCode = String(cached?.countryCode || '').trim().toUpperCase();
        if (ipCountryCode) {
            const byIp = this.detectCurrencyFromCountryCode(ipCountryCode);
            return byIp || 'USD';
        }
        return this.detectDefaultCurrencyFromLocale();
    }

    private detectDefaultCurrencyFromLocale(): Currency {
        try {
            const locale =
                Intl.DateTimeFormat().resolvedOptions().locale
                || (typeof navigator !== 'undefined' ? navigator.language : '')
                || '';

            const region = locale.split(/[-_]/)[1] || '';
            const code = region.toUpperCase();
            const mapped = this.detectCurrencyFromCountryCode(code);
            if (mapped) return mapped;
        } catch {
            // ignore locale failures
        }
        return 'USD';
    }

    private detectCurrencyFromCountryCode(code: string): Currency | null {
        const normalized = String(code || '').trim().toUpperCase();
        if (!normalized) return null;

        const direct: Record<string, Currency> = {
            AE: 'AED',
            SA: 'SAR',
            EG: 'EGP',
            QA: 'QAR',
            GB: 'GBP',
            US: 'USD',
        };

        const mapped = direct[normalized];
        if (mapped) return mapped;

        const euroRegions = new Set([
            'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT', 'LT', 'LU',
            'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
        ]);
        if (euroRegions.has(normalized)) return 'EUR';
        return null;
    }

    private async maybeDetectCurrencyFromIp(): Promise<void> {
        // Only auto-detect when the user has not explicitly chosen a currency.
        const saved = (localStorage.getItem('currency') as Currency) || null;
        if (saved && this.supported.includes(saved)) return;

        const cached = this.geoService.peekGeoFromIpCache();
        const cachedCountry = String(cached?.countryCode || '').trim().toUpperCase();
        if (cachedCountry) return;

        try {
            const geo = await this.geoService.getCountryFromIp();
            const savedAfter = (localStorage.getItem('currency') as Currency) || null;
            if (savedAfter && this.supported.includes(savedAfter)) return;
            const countryCode = String(geo?.countryCode || '').trim().toUpperCase();
            const mapped = this.detectCurrencyFromCountryCode(countryCode);
            const next = mapped || 'USD';
            if (next === this.currentCurrency()) return;
            this.currentCurrency.set(next);
            this.currencyChanges.next(next);
        } catch {
            // ignore geo failures
        }
    }

    private loadCachedRates(): void {
        try {
            const stored = localStorage.getItem(this.ratesStorageKey) || localStorage.getItem(this.legacyRatesKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed?.rates) {
                    const incoming: Record<string, number> = {};
                    this.supported.forEach((code) => {
                        const val = parsed.rates[code];
                        if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
                            incoming[code] = val;
                        }
                    });
                    this.rates = incoming;
                }
                if (parsed?.base) {
                    this.baseCurrency = parsed.base;
                }
                if (parsed?.timestamp) {
                    this.lastFetchedAt = Number(parsed.timestamp) || 0;
                }
            }
            if (!this.lastFetchedAt) {
                const legacyTimestamp = localStorage.getItem(this.legacyTimestampKey);
                const parsedTs = legacyTimestamp ? Number(legacyTimestamp) : 0;
                if (Number.isFinite(parsedTs) && parsedTs > 0) {
                    this.lastFetchedAt = parsedTs;
                }
            }
        } catch {
            /* ignore */
        }
    }

    private storeRates(rates: Record<string, number>, base: string): void {
        const payload = {
            rates,
            base,
            timestamp: Date.now()
        };
        this.lastFetchedAt = payload.timestamp;
        try {
            localStorage.setItem(this.ratesStorageKey, JSON.stringify(payload));
            localStorage.setItem(this.legacyTimestampKey, this.lastFetchedAt.toString());
        } catch {
            /* ignore */
        }
    }

    private maybeRefreshRates(force = false): void {
        const isStale = force || !this.lastFetchedAt || (Date.now() - this.lastFetchedAt) > this.staleMs;
        if (!isStale || this.inflight) return;
        this.inflight = this.fetchRatesFromApi();
    }

    setCurrency(currency: Currency): void {
        if (!currency || !this.supported.includes(currency)) return;
        this.currentCurrency.set(currency);
        this.currencyChanges.next(currency);
        localStorage.setItem('currency', currency);
        this.maybeRefreshRates();
    }

    convert(amount: number, from: string = 'USD', to: string = this.currentCurrency()): number {
        return this.convertForDisplay(amount, from, to).amount;
    }

    // Keep the denomination attached to the value, including when FX is unavailable.
    convertForDisplay(amount: number, from: string = 'USD', to: string = this.currentCurrency()): { amount: number; currency: string } {
        const fromCode = String(from || 'USD').trim().toUpperCase();
        const toCode = String(to || this.currentCurrency()).trim().toUpperCase();
        const original = { amount: Number.isFinite(amount) ? amount : 0, currency: fromCode };
        if (fromCode === toCode) return original;
        const fromRate = this.rates[fromCode];
        const toRate = this.rates[toCode];
        if (!Number.isFinite(fromRate) || !Number.isFinite(toRate) || fromRate <= 0 || toRate <= 0) return original;
        const converted = original.amount / fromRate * toRate;
        return Number.isFinite(converted) ? { amount: converted, currency: toCode } : original;
    }

    format(amount: number, currency: string = this.currentCurrency()): string {
        const normalized = String(currency || 'USD').trim().toUpperCase();
        try {
            const formatted = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: normalized,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(amount);

            // Some currency formats use NBSP/NNSP between code and amount; swap for normal spaces to allow wrapping.
            return formatted.replace(/[\u00A0\u202F]/g, ' ');
        } catch {
            const rounded = Math.round(amount);
            return `${normalized} ${rounded.toLocaleString('en-US')}`;
        }
    }

    private fetchRatesFromApi() {
        return this.http.get<any>(this.rateApiUrl, { withCredentials: false }).pipe(
            map(response => {
                const rates = response?.rates || response?.conversion_rates || null;
                const base = response?.base || response?.base_code || 'USD';
                return rates ? { rates, base } : null;
            }),
            catchError(error => {
                console.warn('Failed to fetch currency rates from Public API:', error);
                return of(null);
            })
        ).subscribe((payload) => {
            if (!payload?.rates) {
                this.inflight = null;
                return;
            }
            const filtered: Record<string, number> = {};
            this.supported.forEach(code => {
                const val = payload.rates[code];
                if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
                    filtered[code] = val;
                }
            });
            if (Object.keys(filtered).length) {
                const baseRaw = String(payload.base || 'USD').trim().toUpperCase();
                const base = this.supported.includes(baseRaw as Currency) ? (baseRaw as Currency) : 'USD';
                filtered[base] = filtered[base] || 1;
                this.baseCurrency = base;
                this.rates = filtered;
                this.storeRates(filtered, base);
            }
            this.inflight = null;
        });
    }
}
